import { useMemo } from 'react';
import type { Monitor } from '../types/Monitor';
import type { UserMonitorConfig } from '../types/UserMonitorConfig';
import { calculatePpi, calculatePreviewScale } from '../utils/ppiCalculator';

export interface ScaleFactorResult {
  /** PPI of the user's physical screen */
  userPpi: number;
  /** PPI of the target monitor */
  targetPpi: number;
  /**
   * CSS scale factor to apply to the preview container.
   * `transform: scale(previewScale)` makes UI elements appear at their true physical size.
   */
  previewScale: number;
  /** Logical resolution of the target monitor in the chosen scaling preset */
  logicalResolution: { logicalW: number; logicalH: number };
}

/**
 * Derives all scale-related values for the real-scale preview.
 *
 * @param userConfig       The user's own monitor specification (includes scalingLogicalW/H)
 * @param targetMonitor    The monitor being previewed
 * @param targetPreset     The chosen scaling preset for the target monitor
 */
export function useScaleFactor(
  userConfig: UserMonitorConfig,
  targetMonitor: Monitor,
  targetPreset: { logicalW: number; logicalH: number }
): ScaleFactorResult {
  return useMemo(() => {
    const userPpi = calculatePpi(
      userConfig.nativeResolutionW,
      userConfig.nativeResolutionH,
      userConfig.diagonalInches
    );

    const targetPpi = targetMonitor.ppi;

    // Scaling factor = native / logical.
    // For the target this comes from the selected preset.
    // For the user this comes from their stored logical resolution.
    const targetScalingFactor = targetMonitor.nativeResolutionW / targetPreset.logicalW;
    const userScalingFactor   = userConfig.nativeResolutionW / userConfig.scalingLogicalW;

    const previewScale = calculatePreviewScale(userPpi, targetPpi, targetScalingFactor, userScalingFactor);

    return {
      userPpi,
      targetPpi,
      previewScale,
      logicalResolution: { logicalW: targetPreset.logicalW, logicalH: targetPreset.logicalH },
    };
  }, [userConfig, targetMonitor, targetPreset]);
}

